import React, { useState } from 'react';
import { node, string } from 'prop-types';
import classNames from 'classnames';

import css from './Code.module.css';

// Copy button component
const CopyButton = ({ textToCopy }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <button
      className={css.copyButton}
      onClick={handleCopy}
      title="Copy to clipboard"
      aria-label="Copy to clipboard"
    >
      {copied ? (
        <svg className={css.copyIcon} viewBox="0 0 16 16" fill="currentColor">
          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
        </svg>
      ) : (
        <svg className={css.copyIcon} viewBox="0 0 16 16" fill="currentColor">
          <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z" />
          <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z" />
        </svg>
      )}
    </button>
  );
};

const defaultPropsCode = {
  rootClassName: null,
  className: null,
  sellerId: null,
  websiteDomain: null,
};

const propTypesCode = {
  rootClassName: string,
  className: string,
  children: node.isRequired,
  sellerId: string,
  websiteDomain: string,
};

/**
 * HTML element <code> represents an inline code.
 * It is marked in markdown with single backticks: some `inline code`
 */
export const Code = React.forwardRef((props, ref) => {
  const { className, rootClassName, sellerId, websiteDomain, ...otherProps } = props;
  const classes = classNames(rootClassName || css.code, className);

  // Replace template variables in children content
  if (otherProps.children && typeof otherProps.children[0] === 'string') {
    let content = otherProps.children[0];
    if (sellerId) {
      content = content.replace(/{storeId}/g, sellerId);
    }
    if (websiteDomain) {
      content = content.replace(/{websitedomain}/g, websiteDomain);
    }
    otherProps.children = [content];
  }

  return <code className={classes} {...otherProps} />;
});
Code.displayName = 'Code';
Code.defaultProps = defaultPropsCode;
Code.propTypes = propTypesCode;

/**
 * HTML element <pre> represents a preformatted text.
 * Codeblock in markdown is rendered with <pre> tag.
 */
export const CodeBlock = React.forwardRef((props, ref) => {
  const { className, rootClassName, sellerId, websiteDomain, ...otherProps } = props;
  const classes = classNames(rootClassName || css.codeBlock, className);

  // Helper function to recursively replace template variables in nested content
  const replaceTemplateVariables = content => {
    if (typeof content === 'string') {
      let updatedContent = content;
      if (sellerId) {
        updatedContent = updatedContent.replace(/{storeId}/g, sellerId);
      }
      if (websiteDomain) {
        updatedContent = updatedContent.replace(/{websitedomain}/g, websiteDomain);
      }
      return updatedContent;
    }

    if (React.isValidElement(content)) {
      return React.cloneElement(content, {
        ...content.props,
        children: React.Children.map(content.props.children, replaceTemplateVariables),
      });
    }

    if (Array.isArray(content)) {
      return content.map(replaceTemplateVariables);
    }

    return content;
  };

  // Replace template variables in children
  if (otherProps.children) {
    otherProps.children = React.Children.map(otherProps.children, replaceTemplateVariables);
  }

  // Extract text content for copy functionality
  const getTextContent = children => {
    if (typeof children === 'string') {
      return children;
    }
    if (React.isValidElement(children)) {
      return getTextContent(children.props.children);
    }
    if (Array.isArray(children)) {
      return children.map(getTextContent).join('');
    }
    return '';
  };

  const textContent = getTextContent(otherProps.children);

  return (
    <div className={css.codeBlockContainer}>
      <CopyButton textToCopy={textContent} />
      <pre className={classes} {...otherProps} ref={ref} />
    </div>
  );
});
CodeBlock.displayName = 'CodeBlock';
CodeBlock.defaultProps = defaultPropsCode;
CodeBlock.propTypes = propTypesCode;
